/**
 * Live markers for the twins that move (trucks, say) on a 2D twin map: never clustered, above the
 * other markers, and with a nose that points the way each one heads. Put it inside a
 * `TurboTwinMap`, next to a `ClusteredMarkersLayer` for the twins that stand still.
 *
 * The host passes `movers` from its own telemetry on every update. A marker is found by `id` and
 * moved in place; one comes with a new id and goes with its id, and none is drawn again on a
 * tick. The nose glides to a new heading (0.4 s) the short way round, and a turn under one degree
 * leaves it.
 */
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { MARKER_COLORS, type MarkerColors } from './makeMarkerIcon';
import { MoverMarkers, type MoverEntry } from './moverMarkers';

export interface MoversLayerProps<M extends MoverEntry = MoverEntry> {
  /** The moving twins, at their latest positions. */
  movers: readonly M[];
  /**
   * A mover's marker colours; the nose takes the stroke. CSS colours: they go into inline styles
   * as they are, so they must not come from user input. Default: `MARKER_COLORS` for every marker.
   */
  colorOf?: (mover: M) => MarkerColors;
  /** A marker click. */
  onClick?: (id: string) => void;
  /** The markers take clicks and hover. False lets a click through to the map, with no tooltip. Default true. */
  interactive?: boolean;
  /**
   * Lifts the markers above the others (Leaflet stacks markers by latitude). Default 1000: above
   * the twin markers and their cluster bubbles, below `ClusteredMarkersLayer`'s selected twin (100 000).
   */
  zIndexOffset?: number;
}

const defaultColorOf = () => MARKER_COLORS;

/** The moving twins' markers, in a layer of their own. */
export function MoversLayer<M extends MoverEntry>({
  movers,
  colorOf = defaultColorOf,
  onClick,
  interactive = true,
  zIndexOffset = 1000,
}: MoversLayerProps<M>): null {
  const map = useMap();
  const markersRef = useRef<MoverMarkers<M> | null>(null);
  // The markers' click handlers read the latest onClick, so a new one re-creates nothing.
  const onClickRef = useRef(onClick);
  useEffect(() => {
    onClickRef.current = onClick;
  });

  useEffect(() => {
    const layer = L.layerGroup().addTo(map);
    markersRef.current = new MoverMarkers<M>(layer, (id) => onClickRef.current?.(id));
    return () => {
      map.removeLayer(layer);
      markersRef.current = null;
    };
  }, [map]);

  // `map` too: a new map gets a new, empty layer, which this fills.
  useEffect(() => {
    markersRef.current?.update(movers, { colorOf, zIndexOffset, interactive });
  }, [map, movers, colorOf, zIndexOffset, interactive]);

  return null;
}
